import React, { useState, useEffect } from 'react';
import api, { BASE_URL } from '../../utils/api';
import { toast } from 'react-toastify';
import { Plus, Edit2, Trash2, Gift, Star, Award, Ticket, ShoppingBag, ExternalLink, Upload, Image, Package, CreditCard, Heart, TrendingUp, Coins, AlertTriangle, DollarSign } from 'lucide-react';

const StoreTab = () => {
  const [items, setItems] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [pointValue, setPointValue] = useState(0.10); // Default: 10 points = $1
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('overview');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    points_cost: 100,
    item_type: 'affiliate',
    affiliate_url: '',
    badge_id: '',
    quantity_available: null,
    is_active: true,
    image_url: '',
    size_options: '',
    color_options: ''
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [itemsRes, redemptionsRes, analyticsRes] = await Promise.all([
        api.get('/store/items'),
        api.get('/admin/store/redemptions'),
        api.get('/admin/store/analytics')
      ]);
      setItems(itemsRes.data.items || []);
      setRedemptions(redemptionsRes.data.redemptions || []);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching store data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        points_cost: parseInt(formData.points_cost),
        quantity_available: formData.quantity_available ? parseInt(formData.quantity_available) : null
      };

      if (editingItem) {
        await api.put(`/admin/store/items/${editingItem.id}`, payload);
        toast.success('Item updated!');
      } else {
        await api.post('/admin/store/items', payload);
        toast.success('Item created!');
      }
      
      setShowModal(false);
      setEditingItem(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save item');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      description: item.description || '',
      points_cost: item.points_cost || 100,
      item_type: item.item_type || 'affiliate',
      affiliate_url: item.affiliate_url || '',
      badge_id: item.badge_id || '',
      quantity_available: item.quantity_available || null,
      is_active: item.is_active !== false,
      image_url: item.image_url || '',
      size_options: item.size_options || '',
      color_options: item.color_options || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await api.delete(`/admin/store/items/${itemId}`);
      toast.success('Item deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const updateRedemptionStatus = async (redemptionId, newStatus) => {
    try {
      await api.put(`/admin/store/redemptions/${redemptionId}`, { status: newStatus });
      
      if (newStatus === 'cancelled') {
        toast.success('Order cancelled and points refunded');
      } else if (newStatus === 'shipped') {
        toast.success('Marked as shipped');
      } else {
        toast.success('Marked as fulfilled');
      }
      
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
      console.error(error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      points_cost: 100,
      item_type: 'affiliate',
      affiliate_url: '',
      badge_id: '',
      quantity_available: null,
      is_active: true,
      image_url: '',
      size_options: '',
      color_options: ''
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large. Max 5MB');
      return;
    }

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await api.post('/admin/store/items/upload-image', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData({ ...formData, image_url: response.data.image_url });
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const getItemIcon = (type) => {
    switch (type) {
      case 'badge': return <Award className="w-5 h-5 text-purple-400" />;
      case 'ad_credit': return <Star className="w-5 h-5 text-blue-400" />;
      case 'affiliate': return <Gift className="w-5 h-5 text-green-400" />;
      case 'prize_entry': return <Ticket className="w-5 h-5 text-yellow-400" />;
      case 'physical': return <Package className="w-5 h-5 text-orange-400" />;
      case 'gift_card': return <CreditCard className="w-5 h-5 text-pink-400" />;
      case 'charity': return <Heart className="w-5 h-5 text-red-400" />;
      default: return <ShoppingBag className="w-5 h-5 text-gray-400" />;
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-400">Loading store data...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Points Store Management</h2>
          <p className="text-gray-500 text-sm mt-1">
            Manage rewards users can redeem with their points
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setEditingItem(null); setShowModal(true); }}
          className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-black font-bold py-2 px-4 rounded transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Item
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-4 mb-6 border-b border-gray-800">
        <button
          onClick={() => setActiveSubTab('overview')}
          className={`pb-2 px-1 font-mono text-sm transition-colors ${
            activeSubTab === 'overview'
              ? 'text-[#C5A52A] border-b-2 border-[#C5A52A]'
              : 'text-gray-500 hover:text-gray-300'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveSubTab('items')}
          className={`pb-2 px-1 font-mono text-sm transition-colors ${
            activeSubTab === 'items'
              ? 'text-[#C5A52A] border-b-2 border-[#C5A52A]'
              : 'text-gray-500 hover:text-gray-300'
          }`}
        >
          Store Items ({items.length})
        </button>
        <button
          onClick={() => setActiveSubTab('redemptions')}
          className={`pb-2 px-1 font-mono text-sm transition-colors ${
            activeSubTab === 'redemptions'
              ? 'text-[#C5A52A] border-b-2 border-[#C5A52A]'
              : 'text-gray-500 hover:text-gray-300'
          }`}
        >
          Redemptions ({redemptions.filter(r => r.status === 'pending').length} pending)
        </button>
      </div>

      {/* Overview Tab */}
      {activeSubTab === 'overview' && analytics && (
        <div className="space-y-6">
          {/* Point Value Setting */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Point Value (for liability calculation)</p>
                <p className="text-xs text-gray-600 mt-1">e.g., 0.10 means 10 points = $1.00</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={pointValue}
                  onChange={(e) => setPointValue(parseFloat(e.target.value) || 0)}
                  className="w-24 bg-gray-800 border border-gray-700 rounded px-3 py-1 text-white text-right"
                />
                <span className="text-gray-500 text-sm">per point</span>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Points Issued */}
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-green-900/30 rounded">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                </div>
                <span className="text-sm text-gray-400">Total Points Issued</span>
              </div>
              <p className="text-3xl font-bold text-white">{analytics.total_points_issued?.toLocaleString() || 0}</p>
              <p className="text-xs text-gray-500 mt-1">
                ${((analytics.total_points_issued || 0) * pointValue).toFixed(2)} value
              </p>
            </div>

            {/* Points Redeemed/Spent */}
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-blue-900/30 rounded">
                  <Coins className="w-5 h-5 text-blue-400" />
                </div>
                <span className="text-sm text-gray-400">Points Redeemed</span>
              </div>
              <p className="text-3xl font-bold text-white">{analytics.total_points_spent?.toLocaleString() || 0}</p>
              <p className="text-xs text-gray-500 mt-1">
                ${((analytics.total_points_spent || 0) * pointValue).toFixed(2)} fulfilled
              </p>
            </div>

            {/* Outstanding/Available Points */}
            <div className="bg-gray-900 border border-yellow-900/50 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-yellow-900/30 rounded">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                </div>
                <span className="text-sm text-gray-400">Outstanding Points</span>
              </div>
              <p className="text-3xl font-bold text-yellow-400">{analytics.total_points_available?.toLocaleString() || 0}</p>
              <p className="text-xs text-yellow-600 mt-1">
                Potential liability
              </p>
            </div>

            {/* Liability Value */}
            <div className="bg-gray-900 border border-red-900/50 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-red-900/30 rounded">
                  <DollarSign className="w-5 h-5 text-red-400" />
                </div>
                <span className="text-sm text-gray-400">Liability Value</span>
              </div>
              <p className="text-3xl font-bold text-red-400">
                ${((analytics.total_points_available || 0) * pointValue).toFixed(2)}
              </p>
              <p className="text-xs text-red-600 mt-1">
                At ${pointValue}/point
              </p>
            </div>
          </div>

          {/* Additional Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <p className="text-sm text-gray-400 mb-1">Total Users with Points</p>
              <p className="text-2xl font-bold text-white">{analytics.users_with_points || 0}</p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <p className="text-sm text-gray-400 mb-1">Total Redemptions</p>
              <p className="text-2xl font-bold text-white">{analytics.total_redemptions || 0}</p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <p className="text-sm text-gray-400 mb-1">Pending Fulfillment</p>
              <p className="text-2xl font-bold text-yellow-400">{analytics.pending_redemptions || 0}</p>
            </div>
          </div>

          {/* Future: Buy Points note */}
          <div className="bg-gray-800/50 border border-dashed border-gray-700 rounded-lg p-4 text-center">
            <p className="text-gray-500 text-sm">
              Future feature: Allow users to purchase points directly
            </p>
          </div>
        </div>
      )}

      {/* Items Tab */}
      {activeSubTab === 'items' && (
        <div className="space-y-4">
          {items.length === 0 ? (
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-12 text-center">
              <ShoppingBag className="w-12 h-12 text-gray-700 mx-auto mb-3" />
              <p className="text-gray-400">No store items yet</p>
              <p className="text-gray-500 text-sm mt-1">Click "Add Item" to create your first reward</p>
            </div>
          ) : (
            items.map((item) => (
              <div 
                key={item.id}
                className={`bg-gray-900 border rounded-lg p-4 flex items-center gap-4 ${
                  item.is_active ? 'border-gray-800' : 'border-red-900/50 opacity-60'
                }`}
              >
                {/* Item Image or Icon */}
                {item.image_url ? (
                  <img 
                    src={item.image_url} 
                    alt={item.name}
                    className="w-12 h-12 rounded object-cover"
                  />
                ) : (
                  <div className="p-2 bg-gray-800 rounded">
                    {getItemIcon(item.item_type)}
                  </div>
                )}
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-white">{item.name}</h3>
                    {!item.is_active && (
                      <span className="text-xs bg-red-900/50 text-red-400 px-2 py-0.5 rounded">INACTIVE</span>
                    )}
                  </div>
                  <p className="text-gray-400 text-sm truncate">{item.description}</p>
                  <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 font-mono">
                    <span className="text-[#C5A52A]">{item.points_cost} pts</span>
                    <span>{item.item_type}</span>
                    {item.quantity_available !== null && (
                      <span>{item.quantity_available} remaining</span>
                    )}
                    {item.affiliate_url && (
                      <a 
                        href={item.affiliate_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
                      >
                        Link <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(item)}
                    className="p-2 bg-gray-800 hover:bg-gray-700 rounded transition-colors"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4 text-gray-400" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-2 bg-gray-800 hover:bg-red-900/50 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Redemptions Tab */}
      {activeSubTab === 'redemptions' && (
        <div className="space-y-4">
          {redemptions.length === 0 ? (
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-12 text-center">
              <Gift className="w-12 h-12 text-gray-700 mx-auto mb-3" />
              <p className="text-gray-400">No redemptions yet</p>
            </div>
          ) : (
            redemptions.map((r) => {
              const shipping = r.fulfillment_data?.shipping_info;
              const isPhysical = r.store_items?.item_type === 'physical';
              const isGiftCard = r.store_items?.item_type === 'gift_card';
              
              return (
                <div 
                  key={r.id}
                  className={`bg-gray-900 border rounded-lg overflow-hidden ${
                    r.status === 'pending' ? 'border-yellow-900/50' : 'border-gray-800'
                  }`}
                >
                  {/* Header */}
                  <div className="p-4 flex items-center justify-between border-b border-gray-800">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-white">{r.users?.name || 'Unknown User'}</span>
                        <span className="text-gray-500">•</span>
                        <span className="text-[#C5A52A]">{r.store_items?.name || 'Unknown Item'}</span>
                      </div>
                      <div className="text-xs text-gray-500 font-mono mt-1">
                        {new Date(r.redeemed_at).toLocaleString()} • {r.points_spent} pts • {r.users?.email}
                      </div>
                    </div>
                    <span className={`text-xs font-mono uppercase px-3 py-1 rounded ${
                      r.status === 'fulfilled' ? 'bg-green-900/30 text-green-400' :
                      r.status === 'shipped' ? 'bg-blue-900/30 text-blue-400' :
                      r.status === 'pending' ? 'bg-yellow-900/30 text-yellow-400' :
                      'bg-gray-800 text-gray-400'
                    }`}>
                      {r.status}
                    </span>
                  </div>
                  
                  {/* Shipping Details for Physical Items */}
                  {isPhysical && shipping && (
                    <div className="p-4 bg-orange-900/10 border-b border-gray-800">
                      <p className="text-xs text-orange-400 font-mono uppercase mb-2">Shipping Details</p>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-white font-medium">{shipping.full_name}</p>
                          <p className="text-gray-400">{shipping.address_line1}</p>
                          {shipping.address_line2 && <p className="text-gray-400">{shipping.address_line2}</p>}
                          <p className="text-gray-400">{shipping.city}, {shipping.province} {shipping.postal_code}</p>
                          {shipping.phone && <p className="text-gray-500 text-xs mt-1">Phone: {shipping.phone}</p>}
                        </div>
                        <div>
                          {shipping.selected_size && (
                            <p className="text-gray-300"><span className="text-gray-500">Size:</span> {shipping.selected_size}</p>
                          )}
                          {shipping.selected_color && (
                            <p className="text-gray-300"><span className="text-gray-500">Color:</span> {shipping.selected_color}</p>
                          )}
                          {shipping.notes && (
                            <p className="text-gray-400 text-xs mt-2"><span className="text-gray-500">Notes:</span> {shipping.notes}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Gift Card Note */}
                  {isGiftCard && r.status === 'pending' && (
                    <div className="p-4 bg-pink-900/10 border-b border-gray-800">
                      <p className="text-xs text-pink-400 font-mono">Send gift card code to: <span className="text-white">{r.users?.email}</span></p>
                    </div>
                  )}
                  
                  {/* Actions */}
                  {r.status === 'pending' && (
                    <div className="p-4 flex gap-2">
                      {isPhysical && (
                        <button
                          onClick={() => updateRedemptionStatus(r.id, 'shipped')}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors"
                        >
                          Mark as Shipped
                        </button>
                      )}
                      <button
                        onClick={() => updateRedemptionStatus(r.id, 'fulfilled')}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors"
                      >
                        Mark as Fulfilled
                      </button>
                      <button
                        onClick={() => updateRedemptionStatus(r.id, 'cancelled')}
                        className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded transition-colors"
                      >
                        Cancel & Refund
                      </button>
                    </div>
                  )}
                  
                  {r.status === 'shipped' && (
                    <div className="p-4 flex gap-2">
                      <button
                        onClick={() => updateRedemptionStatus(r.id, 'fulfilled')}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors"
                      >
                        Mark as Delivered/Fulfilled
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-800">
              <h3 className="text-xl font-bold text-white">
                {editingItem ? 'Edit Store Item' : 'Add Store Item'}
              </h3>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Free Month Premium Trial"
                  className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={2}
                  placeholder="What the user gets..."
                  className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white resize-none"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Points Cost *</label>
                  <input
                    type="number"
                    value={formData.points_cost}
                    onChange={(e) => setFormData({ ...formData, points_cost: e.target.value })}
                    required
                    min="1"
                    className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Item Type *</label>
                  <select
                    value={formData.item_type}
                    onChange={(e) => setFormData({ ...formData, item_type: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
                  >
                    <option value="affiliate">Partner Reward (Affiliate Link)</option>
                    <option value="badge">Exclusive Badge</option>
                    <option value="ad_credit">Ad Credit</option>
                    <option value="prize_entry">Prize Draw Entry</option>
                    <option value="physical">Physical Item (T-shirt, merch, etc.)</option>
                    <option value="gift_card">Gift Card</option>
                    <option value="charity">Donation to Local Charity</option>
                  </select>
                </div>
              </div>
              
              {formData.item_type === 'affiliate' && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Affiliate URL *</label>
                  <input
                    type="url"
                    value={formData.affiliate_url}
                    onChange={(e) => setFormData({ ...formData, affiliate_url: e.target.value })}
                    placeholder="https://..."
                    className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
                  />
                  <p className="text-xs text-gray-500 mt-1">User will be redirected here after redeeming</p>
                </div>
              )}
              
              {formData.item_type === 'badge' && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Badge ID</label>
                  <input
                    type="text"
                    value={formData.badge_id}
                    onChange={(e) => setFormData({ ...formData, badge_id: e.target.value })}
                    placeholder="Badge UUID from badge_definitions table"
                    className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
                  />
                </div>
              )}

              {formData.item_type === 'physical' && (
                <div className="space-y-4 p-4 bg-orange-900/10 border border-orange-900/30 rounded">
                  <p className="text-sm text-orange-400 font-medium">Physical Item Options</p>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">
                      Size Options <span className="text-gray-600">(comma-separated)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.size_options}
                      onChange={(e) => setFormData({ ...formData, size_options: e.target.value })}
                      placeholder="e.g., S, M, L, XL, XXL"
                      className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">
                      Color Options <span className="text-gray-600">(comma-separated)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.color_options}
                      onChange={(e) => setFormData({ ...formData, color_options: e.target.value })}
                      placeholder="e.g., Black, White, Red, Blue"
                      className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
                    />
                  </div>
                  <p className="text-xs text-gray-500">Leave blank if not applicable. Users will see dropdowns to select from these options.</p>
                </div>
              )}
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">
                  Quantity Available 
                  <span className="text-gray-600 ml-1">(leave empty for unlimited)</span>
                </label>
                <input
                  type="number"
                  value={formData.quantity_available || ''}
                  onChange={(e) => setFormData({ ...formData, quantity_available: e.target.value || null })}
                  min="0"
                  placeholder="Unlimited"
                  className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">
                  Item Image
                  <span className="text-gray-600 ml-1">(optional)</span>
                </label>
                <div className="flex items-center gap-4">
                  {formData.image_url ? (
                    <div className="relative">
                      <img 
                        src={formData.image_url} 
                        alt="Preview" 
                        className="w-20 h-20 rounded object-cover border border-gray-700"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image_url: '' })}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded border border-dashed border-gray-700 flex items-center justify-center">
                      <Image className="w-6 h-6 text-gray-600" />
                    </div>
                  )}
                  <label className={`flex items-center gap-2 px-4 py-2 rounded cursor-pointer transition-colors ${
                    uploading ? 'bg-gray-700 cursor-wait' : 'bg-gray-800 hover:bg-gray-700'
                  }`}>
                    <Upload className="w-4 h-4" />
                    <span className="text-sm">{uploading ? 'Uploading...' : 'Upload Image'}</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploading}
                      className="hidden"
                    />
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">JPEG, PNG, GIF, or WebP. Max 5MB.</p>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded bg-gray-800 border-gray-700"
                />
                <label htmlFor="is_active" className="text-sm text-gray-400">Active (visible in store)</label>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingItem(null); }}
                  className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 text-black font-bold rounded transition-colors"
                >
                  {editingItem ? 'Update Item' : 'Create Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreTab;
